import { useEffect, useMemo, useState, type JSX } from 'react'
import { apiErrorFrom, bearerToken, can, request, shortDate } from '../lib/api'
import { ErrorBox, Loading } from './DataTable'
import { Dialog, Status, Toggletip } from './kit'

// The files a vendor attached to its answers, on the screen of the person who
// has to review them.
//
// Until this panel existed a supplier could send the certificate a question
// asked for and nobody on our side had a way to see it. The row and the bytes
// were both stored correctly; no route read them.
//
// # WHY THE FILE IS SAVED AND NEVER OPENED
//
// This is the one rule in this file that is not cosmetic. A vendor uploads a
// document, a reviewer opens it, and if the browser renders it, it runs on the
// console's origin with the reviewer's session: the register readable, an
// approval postable.
//
// The API answers with application/octet-stream, nosniff, an attachment
// disposition and a sandbox content security policy, which is what stops that.
// The console must not undo it. So the download here builds an object URL,
// hands it to an anchor carrying the download attribute, and revokes it. It
// never assigns window.location, never calls window.open and never puts the URL
// in an iframe or a preview, because a blob: document inherits the origin of the
// page that created it and would put the file back inside the console after all
// the trouble the API went to.
//
// A fetch is used rather than a plain href because the request has to carry the
// bearer token, and a link cannot.
//
// # WHY NOTHING HERE SHOWS A GREEN TICK
//
// scan_status is PENDING on every row and this deployment has no scanner. The
// panel says that once, plainly, at the top. A chip reading "pending" beside
// each file would be read as "in progress", and the honest word is that nobody
// has looked and nobody is going to.

type AnswerEvidence = {
  id: string
  responseId: string
  questionKey: string
  fileName: string
  contentSha256: string
  sizeBytes: number
  mimeType: string
  scanStatus: string
  filedBy: string
  filedAt: string
}

// What this panel reads of the response. The route also returns the
// assessment reference and the scanning sentence; neither is named here,
// because the header above already carries the reference and the note below
// says the same thing in the console's own words.
type EvidenceList = {
  evidence: AnswerEvidence[]
  count: number
}

function sizeLabel(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

export function AssessmentEvidence({ assessmentId }: { assessmentId: string }): JSX.Element | null {
  const [list, setList] = useState<EvidenceList | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState<string>('')
  const [saveError, setSaveError] = useState<unknown>(null)
  const [about, setAbout] = useState(false)

  // evidence.read since release 3.1: opening what a vendor attached is not
  // the same act as confirming it, and the auditors read it too.
  const allowed = can('evidence.read')

  useEffect(() => {
    if (!allowed) return
    let live = true
    setList(null)
    setError(null)
    request<EvidenceList>('/v1/assessments/' + encodeURIComponent(assessmentId) + '/evidence')
      .then((page) => {
        if (live) setList(page)
      })
      .catch((err) => {
        if (live) setError(err)
      })
    return () => {
      live = false
    }
  }, [assessmentId, allowed])

  const byQuestion = useMemo(() => {
    const groups = new Map<string, AnswerEvidence[]>()
    for (const f of list?.evidence ?? []) {
      const existing = groups.get(f.questionKey)
      if (existing) existing.push(f)
      else groups.set(f.questionKey, [f])
    }
    return [...groups.entries()]
  }, [list])

  // A reader without evidence.read gets no panel rather than an empty one.
  // An empty panel would read as "this vendor attached nothing", which is a
  // different and much worse thing to tell somebody than nothing at all.
  if (!allowed) return null

  async function save(file: AnswerEvidence) {
    setBusy(file.id)
    setSaveError(null)
    let url = ''
    try {
      const res = await fetch('/v1/evidence/' + encodeURIComponent(file.id) + '/content', {
        headers: { Authorization: 'Bearer ' + (await bearerToken()) },
      })
      if (!res.ok) {
        let body: unknown = { error: 'READ_FAILED', message: 'The file could not be read.' }
        try {
          body = await res.json()
        } catch {
          // A refusal that is not JSON is still a refusal, and the status is
          // what the reader needs. Saying so beats an empty alert.
        }
        // The code and the reference, read the way every other refusal is;
        // the ErrorBox below shows both.
        throw apiErrorFrom(res, body, '/v1/evidence/' + file.id + '/content')
      }
      url = URL.createObjectURL(await res.blob())
      const a = document.createElement('a')
      a.href = url
      a.download = file.fileName
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      a.remove()
    } catch (err) {
      setSaveError(err)
    } finally {
      if (url) URL.revokeObjectURL(url)
      setBusy('')
    }
  }

  return (
    <section className="panel panel-flush" aria-labelledby="assessment-evidence-heading">
      <div className="panel-head">
        <h2 id="assessment-evidence-heading" style={{ margin: 0 }}>
          What the vendor sent
        </h2>
        <p className="summary-line" style={{ margin: 0 }}>
          {list === null ? (
            <span className="muted">not read yet</span>
          ) : (
            <>
              <strong>{list.count}</strong> {list.count === 1 ? 'file' : 'files'} attached
            </>
          )}
        </p>
      </div>

      {/* The API sends the same statement in its `scanning` field for anybody
          reading the route directly. It is not printed here as well: one
          sentence said twice reads as boilerplate and stops being read. The
          warning keeps one line on the panel, because it is the thing a
          reviewer must see before saving a file, and the whole of it is one
          press away. */}
      <div className="note warn" style={{ margin: '12px 16px' }}>
        <Status tone="warn">Not scanned</Status> <strong>Untrusted files, open only where an unknown file is safe</strong>{' '}
        <button type="button" className="linkish" onClick={() => setAbout(true)}>
          About these files
        </button>
      </div>
      <Dialog shape="drawer" kind="record" open={about} onClose={() => setAbout(false)} title="About these files">
        <p>
          No scanner is contracted in this deployment, so PENDING means nobody has looked rather than queued. Every one
          of these is an untrusted file from an outside party.
        </p>
        <p>
          They save rather than open, and they are served in a way that stops a document running inside this console.
          Open one on a machine you are willing to open an unknown file on.
        </p>
      </Dialog>

      {error != null ? (
        <div style={{ padding: '0 16px 12px' }}>
          <ErrorBox error={error} what="The files filed against this assessment" />
        </div>
      ) : list === null ? (
        <div style={{ padding: '0 16px 12px' }}>
          <Loading what="the attached files" />
        </div>
      ) : list.count === 0 ? (
        <div className="empty">
          <strong>Nothing attached to this assessment</strong>
          Every answer stands on what the respondent typed{' '}
          <Toggletip label="Where a document was asked for">
            Where a question asked for a document, the Responses panel marks it.
          </Toggletip>
        </div>
      ) : (
        <table className="ev-table">
          <thead>
            <tr>
              <th scope="col">File</th>
              <th scope="col">Size</th>
              <th scope="col">Type</th>
              <th scope="col">Filed by</th>
              <th scope="col">Filed</th>
              <th scope="col">Digest</th>
              <th scope="col" />
            </tr>
          </thead>
          {byQuestion.map(([key, files]) => (
            <tbody key={key}>
              <tr>
                <th className="ev-q" colSpan={7} scope="colgroup">
                  <span className="mono">{key}</span>
                </th>
              </tr>
              {files.map((f) => (
                <tr key={f.id}>
                  <td>{f.fileName}</td>
                  <td className="num">{sizeLabel(f.sizeBytes)}</td>
                  <td className="muted">{f.mimeType || 'unknown'}</td>
                  <td>{f.filedBy || <span className="muted">not recorded</span>}</td>
                  <td>{shortDate(f.filedAt)}</td>
                  {/* Sixteen characters is enough to compare against a digest
                      somebody was quoted, and short enough not to take the row. */}
                  <td className="mono" title={f.contentSha256}>
                    {f.contentSha256.slice(0, 16)}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="linkish"
                      data-action="tprm.assessment.a01"
                      disabled={busy === f.id}
                      onClick={() => void save(f)}
                    >
                      {busy === f.id ? 'Saving' : 'Save file'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      )}

      {saveError != null && (
        <div style={{ padding: '0 16px 12px' }}>
          <ErrorBox error={saveError} what="That file" verb="read back" />
        </div>
      )}
    </section>
  )
}
